"use client";

import { useRef } from "react";
import { BUTTON_SMALL } from "@/components/tool-kit/styles";
import { PLANE_FONT } from "@/components/tools/math/coordinate-plane";
import { downloadPng } from "@/lib/tool-kit/download";
import type { Triangle } from "@/lib/tool-kit/linear-algebra/triangle";
import { CHART_COLORS, CHART_INK } from "@/lib/tool-kit/palette";

const WIDTH = 480;
const HEIGHT = 300;
const MARGIN = 58;
const ARC = 22;
const VERTEX_LETTERS = ["A", "B", "C"] as const;
const SIDE_LETTERS = ["a", "b", "c"] as const;

interface Point {
  x: number;
  y: number;
}

export interface TriangleLabels {
  /** Text next to each side a, b, c, e.g. "a = 5". */
  sides: [string, string, string];
  /** Text inside each angle A, B, C, e.g. "30°". */
  angles: [string, string, string];
  /** Which values were entered (drawn in the "given" colour) and which were solved. */
  givenSides: [boolean, boolean, boolean];
  givenAngles: [boolean, boolean, boolean];
}

/** Vertices to scale in screen coordinates: A at the left, B to its right, C above. */
export function triangleVertices(t: Triangle): [Point, Point, Point] {
  const [, b, c] = t.sides;
  const raw: Point[] = [
    { x: 0, y: 0 },
    { x: c, y: 0 },
    { x: b * Math.cos(t.angles[0]), y: b * Math.sin(t.angles[0]) },
  ];
  const xs = raw.map((p) => p.x);
  const ys = raw.map((p) => p.y);
  const width = Math.max(...xs) - Math.min(...xs);
  const height = Math.max(...ys) - Math.min(...ys);
  const scale = Math.min(
    (WIDTH - 2 * MARGIN) / width,
    (HEIGHT - 2 * MARGIN) / height
  );
  const left = (WIDTH - width * scale) / 2 - Math.min(...xs) * scale;
  const bottom = (HEIGHT + height * scale) / 2 + Math.min(...ys) * scale;
  return raw.map((p) => ({
    x: left + p.x * scale,
    y: bottom - p.y * scale,
  })) as [Point, Point, Point];
}

function unit(from: Point, to: Point): Point {
  const length = Math.hypot(to.x - from.x, to.y - from.y) || 1;
  return { x: (to.x - from.x) / length, y: (to.y - from.y) / length };
}

function Angle({
  at,
  towardA,
  towardB,
  label,
  right,
  color,
  angle,
}: {
  angle: number;
  at: Point;
  towardA: Point;
  towardB: Point;
  label: string;
  right: boolean;
  color: string;
}) {
  const u1 = unit(at, towardA);
  const u2 = unit(at, towardB);
  const start = { x: at.x + u1.x * ARC, y: at.y + u1.y * ARC };
  const end = { x: at.x + u2.x * ARC, y: at.y + u2.y * ARC };
  const sweep = u1.x * u2.y - u1.y * u2.x > 0 ? 1 : 0;
  const bisector = unit({ x: 0, y: 0 }, { x: u1.x + u2.x, y: u1.y + u2.y });
  // Far enough along the bisector that the label clears both sides of a narrow angle.
  const reach = Math.min(90, Math.max(ARC + 16, 24 / Math.sin(angle / 2)));
  const corner = { x: start.x + u2.x * ARC, y: start.y + u2.y * ARC };
  return (
    <g>
      {right ? (
        <path
          d={`M${start.x} ${start.y} L${corner.x} ${corner.y} L${end.x} ${end.y}`}
          fill="none"
          stroke={color}
          strokeWidth={1.5}
        />
      ) : (
        <path
          d={`M${start.x} ${start.y} A${ARC} ${ARC} 0 0 ${sweep} ${end.x} ${end.y}`}
          fill="none"
          stroke={color}
          strokeWidth={1.5}
        />
      )}
      <text
        dominantBaseline="middle"
        fill={color}
        fontSize={12}
        fontWeight={600}
        textAnchor="middle"
        x={at.x + bisector.x * reach}
        y={at.y + bisector.y * reach}
      >
        {label}
      </text>
    </g>
  );
}

/** A triangle drawn to scale; given parts in blue, solved parts in orange. */
export function TriangleDiagram({
  triangle,
  labels,
  title,
  downloadLabel,
  fileName,
}: {
  triangle: Triangle;
  labels: TriangleLabels;
  title: string;
  downloadLabel: string;
  fileName: string;
}) {
  const ref = useRef<SVGSVGElement>(null);
  const v = triangleVertices(triangle);
  const centre = {
    x: (v[0].x + v[1].x + v[2].x) / 3,
    y: (v[0].y + v[1].y + v[2].y) / 3,
  };
  const colour = (given: boolean) =>
    given ? CHART_COLORS[0] : CHART_COLORS[3];
  return (
    <div className="space-y-3">
      <svg
        aria-label={title}
        className="h-auto w-full max-w-xl"
        fontFamily={PLANE_FONT}
        ref={ref}
        role="img"
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        xmlns="http://www.w3.org/2000/svg"
      >
        <title>{title}</title>
        <rect fill={CHART_INK.background} height={HEIGHT} width={WIDTH} />
        <path
          d={`M${v[0].x} ${v[0].y} L${v[1].x} ${v[1].y} L${v[2].x} ${v[2].y} Z`}
          fill="rgba(0,114,178,0.07)"
        />
        {[0, 1, 2].map((i) => {
          const p = v[(i + 1) % 3];
          const q = v[(i + 2) % 3];
          const mid = { x: (p.x + q.x) / 2, y: (p.y + q.y) / 2 };
          const out = unit(centre, mid);
          return (
            <g key={SIDE_LETTERS[i]}>
              <line
                stroke={colour(labels.givenSides[i])}
                strokeWidth={3}
                x1={p.x}
                x2={q.x}
                y1={p.y}
                y2={q.y}
              />
              <text
                dominantBaseline="middle"
                fill={colour(labels.givenSides[i])}
                fontSize={13}
                fontWeight={600}
                textAnchor="middle"
                x={mid.x + out.x * 24}
                y={mid.y + out.y * 18}
              >
                {labels.sides[i]}
              </text>
            </g>
          );
        })}
        {[0, 1, 2].map((i) => {
          const out = unit(centre, v[i]);
          return (
            <g key={VERTEX_LETTERS[i]}>
              <Angle
                angle={triangle.angles[i]}
                at={v[i]}
                color={colour(labels.givenAngles[i])}
                label={labels.angles[i]}
                right={Math.abs(triangle.angles[i] - Math.PI / 2) < 1e-6}
                towardA={v[(i + 1) % 3]}
                towardB={v[(i + 2) % 3]}
              />
              <text
                dominantBaseline="middle"
                fill={CHART_INK.text}
                fontSize={15}
                fontStyle="italic"
                fontWeight={700}
                textAnchor="middle"
                x={v[i].x + out.x * 16}
                y={v[i].y + out.y * 16}
              >
                {VERTEX_LETTERS[i]}
              </text>
            </g>
          );
        })}
      </svg>
      <button
        className={BUTTON_SMALL}
        onClick={() => {
          if (ref.current) {
            downloadPng(fileName, ref.current).catch(() => undefined);
          }
        }}
        type="button"
      >
        {downloadLabel}
      </button>
    </div>
  );
}
